<script setup>
// Fora do horário, o agente vira outro agente.
//
// Até aqui "fora do horário" só tinha dois desfechos, e os dois são ruins. Ou o agente segue
// igual — com o prompt de vendas e o catálogo inteiro de tools, prometendo resolver às 23h o
// que ninguém vai resolver — ou sai do ar pela janela de manutenção, e quem escreve recebe um
// aviso e mais nada. A triagem é o meio termo: ele ATENDE, com outro prompt, colhendo o que o
// humano da manhã precisa, e sem as tools que movem coisa no mundo.
//
// Três decisões que a tela precisa deixar visíveis, porque são elas que surpreendem:
//
//   1. o prompt da triagem SUBSTITUI o do agente, não se soma a ele. Um prompt de vendas com
//      "além disso, é madrugada" colado no fim continua tentando vender;
//   2. tool não marcada é tool que o agente NEM VÊ na janela — não é recusa, é ausência. Por
//      isso o padrão de lista vazia é "nenhuma", e o aviso ao lado diz isso;
//   3. a trava existe para a conversa não trocar de personalidade no meio da frase. Quem
//      escreve 17h58 e de novo 18h01 continua com o mesmo agente pelo tempo configurado.
//
// Toda regra de campo aqui é a MESMA do controller (TRIAGEM_SCHEMA) e a do runtime
// (`runtime/triagem.py`). Divergir faria a tela salvar em verde e o agente rodar com outra
// coisa — e a diferença só apareceria como nota no rastro de um turno, de madrugada.
import { ref, computed, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { mensagemDoErro } from '../erros';
import powerAgentsApi from 'dashboard/api/powerAgents';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import SectionShell from './SectionShell.vue';
import { useRascunhoDaSecao } from '../useRascunhoDaSecao';

const props = defineProps({
  agent: { type: Object, required: true },
});

const { t } = useI18n();
const ctx = inject('powerAgentContext');

// ISO: 1 = segunda … 7 = domingo. É o que o runtime lê (`isoweekday()`) e o que o controller
// valida. Usar o índice do JavaScript (0 = domingo) daria a mesma lista deslocada em um dia,
// e o erro só apareceria no fim de semana — que é justamente quando a triagem importa.
const DIAS = [1, 2, 3, 4, 5, 6, 7];

const bloco = props.agent.config?.triagem || {};
const janelaSalva = bloco.janela || {};

// Nasce DESLIGADA: é o interruptor mestre, e uma feature que troca o prompt do agente não se
// liga sozinha. `?? false` e não `!== false` pelo mesmo motivo.
const ativa = ref(bloco.ativa ?? false);
const systemPrompt = ref(bloco.system_prompt || '');
const travaMinutos = ref(bloco.trava_sessao_minutos ?? 0);
const dias = ref([...(janelaSalva.dias ?? DIAS)]);
const inicio = ref(janelaSalva.inicio || '');
const fim = ref(janelaSalva.fim || '');
const fuso = ref(janelaSalva.fuso || '');
const saving = ref(false);

// As tools que ESTE agente tem. A lista de permitidas é escolha entre elas, não texto livre:
// digitar o nome de uma tool que não existe não daria erro em lugar nenhum — só uma triagem
// sem tool nenhuma, em silêncio.
const toolsDoAgente = computed(() =>
  (props.agent.tools || []).map(b => b?.tool?.name).filter(Boolean)
);
const permitidas = ref((bloco.tools_permitidas || []).filter(nome => nome));

function alternarDia(dia) {
  const i = dias.value.indexOf(dia);
  if (i === -1) dias.value.push(dia);
  else dias.value.splice(i, 1);
}

function alternarTool(nome) {
  const i = permitidas.value.indexOf(nome);
  if (i === -1) permitidas.value.push(nome);
  else permitidas.value.splice(i, 1);
}

// O controller recusa `HH:MM` fora do formato, e o runtime não abre a janela com hora
// ilegível. Mandar string vazia seria recusado; `null` é o "sem limite" que os dois entendem.
const horaOuNula = valor => (valor && valor.trim() ? valor.trim() : null);

const semTool = computed(() => ativa.value && permitidas.value.length === 0);
const semDia = computed(() => ativa.value && dias.value.length === 0);
// Triagem ligada e sem prompt é o único estado em que a tela e o runtime discordariam em
// silêncio: o `null` é salvo sem erro (o controller aceita), e lá a triagem NÃO liga — vira
// nota `triagem:valor_invalido:system_prompt` no rastro de um turno que ninguém vai ler.
// Entrar num modo sem instrução nenhuma é pior que não entrar, então o runtime está certo;
// o que faltava era a tela dizer isso antes de o operador ir dormir achando que configurou.
const semPrompt = computed(() => ativa.value && !systemPrompt.value.trim());

// O que a pessoa edita aqui; a página avisa antes de perder (ver useRascunhoDaSecao).
const { suja, limpar, definirSalvar } = useRascunhoDaSecao(() => ({
  ativa: ativa.value,
  systemPrompt: systemPrompt.value,
  travaMinutos: travaMinutos.value,
  dias: dias.value,
  inicio: inicio.value,
  fim: fim.value,
  fuso: fuso.value,
  permitidas: permitidas.value,
}));

async function save() {
  saving.value = true;
  try {
    const updated = await powerAgentsApi.update(
      ctx.accountId(),
      ctx.agentId(),
      {
        config: {
          ...(props.agent.config || {}),
          triagem: {
            ativa: ativa.value,
            system_prompt: systemPrompt.value.trim() || null,
            tools_permitidas: permitidas.value,
            trava_sessao_minutos: Number(travaMinutos.value) || 0,
            janela: {
              dias: [...dias.value].sort((a, b) => a - b),
              inicio: horaOuNula(inicio.value),
              fim: horaOuNula(fim.value),
              fuso: fuso.value.trim() || null,
            },
          },
        },
      }
    );
    ctx.onSectionSaved(updated);
    // A triagem muda o PROMPT e o catálogo de tools do turno, e os dois vêm da revisão
    // publicada: salvar não basta, é preciso publicar para valer.
    ctx.markPending();
    useAlert(t('POWER_AGENTS.SAVED'));
    limpar();
  } catch (e) {
    useAlert(mensagemDoErro(e, t, t('POWER_AGENTS.SAVE_ERROR')));
  } finally {
    saving.value = false;
  }
}
definirSalvar(save);
</script>

<template>
  <SectionShell
    data-busca="triagem"
    :title="t('POWER_AGENTS.TRIAGEM.TITLE')"
    :description="t('POWER_AGENTS.TRIAGEM.DESCRIPTION')"
    :saving="saving"
    :suja="suja"
    @save="save"
  >
    <div data-busca="triagem-enabled" class="flex items-start gap-3 p-1">
      <Switch v-model="ativa" class="mt-0.5" />
      <div class="min-w-0">
        <span class="block text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.TRIAGEM.ENABLED') }}
        </span>
        <span class="block mt-0.5 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.TRIAGEM.ENABLED_HINT') }}
        </span>
      </div>
    </div>

    <div v-if="ativa" class="flex flex-col gap-5">
      <div data-busca="triagem-prompt">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.TRIAGEM.PROMPT') }}
        </label>
        <textarea
          v-model="systemPrompt"
          rows="6"
          class="reset-base w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10"
          :placeholder="t('POWER_AGENTS.TRIAGEM.PROMPT_PLACEHOLDER')"
        />
        <p v-if="semPrompt" class="mt-2 text-xs text-n-amber-11">
          {{ t('POWER_AGENTS.TRIAGEM.NO_PROMPT') }}
        </p>
        <p v-else class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.TRIAGEM.PROMPT_HINT') }}
        </p>
      </div>

      <div data-busca="triagem-dias">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.TRIAGEM.DAYS') }}
        </label>
        <div class="flex flex-wrap gap-2">
          <button
            v-for="dia in DIAS"
            :key="dia"
            type="button"
            class="px-3 py-1.5 text-xs font-medium border rounded-lg"
            :class="
              dias.includes(dia)
                ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                : 'border-n-weak text-n-slate-10'
            "
            @click="alternarDia(dia)"
          >
            {{ t(`POWER_AGENTS.TRIAGEM.DAY_NAMES.D${dia}`) }}
          </button>
        </div>
        <p v-if="semDia" class="mt-2 text-xs text-n-amber-11">
          {{ t('POWER_AGENTS.TRIAGEM.NO_DAYS') }}
        </p>
        <p v-else class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.TRIAGEM.DAYS_HINT') }}
        </p>
      </div>

      <div data-busca="triagem-horario" class="flex flex-wrap gap-4">
        <div class="min-w-32">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.TRIAGEM.START') }}
          </label>
          <input
            v-model="inicio"
            type="time"
            class="reset-base w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
        </div>
        <div class="min-w-32">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.TRIAGEM.END') }}
          </label>
          <input
            v-model="fim"
            type="time"
            class="reset-base w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
          />
        </div>
        <div class="flex-1 min-w-48">
          <label class="block mb-1 text-sm font-medium text-n-slate-12">
            {{ t('POWER_AGENTS.TRIAGEM.TIMEZONE') }}
          </label>
          <input
            v-model="fuso"
            type="text"
            class="reset-base w-full px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand placeholder:text-n-slate-10"
            :placeholder="t('POWER_AGENTS.TRIAGEM.TIMEZONE_PLACEHOLDER')"
          />
        </div>
      </div>
      <!-- O fim é EXCLUSIVO e o início inclusivo: às 18:00 de uma janela 18:00–08:00 a
           triagem já vale, e às 08:00 já não vale. Dito na tela porque a borda de um minuto
           é invisível até alguém reclamar. -->
      <p class="-mt-3 text-xs text-n-slate-10">
        {{ t('POWER_AGENTS.TRIAGEM.HOURS_HINT') }}
      </p>

      <div data-busca="triagem-tools">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.TRIAGEM.TOOLS') }}
        </label>
        <div v-if="toolsDoAgente.length" class="flex flex-wrap gap-2">
          <button
            v-for="nome in toolsDoAgente"
            :key="nome"
            type="button"
            class="px-3 py-1.5 text-xs font-medium border rounded-lg"
            :class="
              permitidas.includes(nome)
                ? 'border-n-brand bg-n-alpha-2 text-n-slate-12'
                : 'border-n-weak text-n-slate-10'
            "
            @click="alternarTool(nome)"
          >
            {{ nome }}
          </button>
        </div>
        <p v-else class="text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.TRIAGEM.NO_TOOLS_AT_ALL') }}
        </p>
        <p v-if="semTool" class="mt-2 text-xs text-n-amber-11">
          {{ t('POWER_AGENTS.TRIAGEM.NO_TOOLS') }}
        </p>
        <p v-else class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.TRIAGEM.TOOLS_HINT') }}
        </p>
      </div>

      <div data-busca="triagem-trava">
        <label class="block mb-1 text-sm font-medium text-n-slate-12">
          {{ t('POWER_AGENTS.TRIAGEM.LOCK') }}
        </label>
        <input
          v-model="travaMinutos"
          type="number"
          min="0"
          class="reset-base w-32 px-3 py-2 text-sm border rounded-lg outline-none border-n-weak bg-n-background text-n-slate-12 focus:border-n-brand"
        />
        <p class="mt-1 text-xs text-n-slate-10">
          {{ t('POWER_AGENTS.TRIAGEM.LOCK_HINT') }}
        </p>
      </div>
    </div>
  </SectionShell>
</template>
